import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Button } from "./ui/button";
import { Switch } from "./ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { DepotField } from "./DepotField";
import { api, formatApiError } from "../lib/api";

const EMPTY = { name: "", email: "", password: "", role: "employee", depot_id: "", active: true };

export function UserDialog({ open, onOpenChange, user, onSaved }) {
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (open) {
      setForm(user ? { ...EMPTY, ...user, password: "", depot_id: user.depot_id || "", active: user.pending ? true : user.active } : EMPTY);
    }
  }, [open, user]);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (user) {
        const body = { name: form.name, role: form.role, depot_id: form.depot_id || null, active: form.active };
        if (form.password) body.password = form.password;
        await api.put(`/users/${user.id}`, body);
      } else {
        await api.post("/users", { ...form, depot_id: form.depot_id || null });
      }
      toast.success(user ? "User updated" : "Employee account created");
      onOpenChange(false);
      onSaved();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="user-dialog">
        <DialogHeader><DialogTitle className="font-display text-2xl uppercase">{user?.pending ? "Approve request" : user ? "Edit user" : "New user"}</DialogTitle></DialogHeader>
        {user?.pending && (
          <div className="rounded-md border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-sm text-amber-200" data-testid="user-pending-note">
            {user.email} signed up. Assign a depot and keep Active on, then save to approve.
          </div>
        )}
        <form onSubmit={submit} className="space-y-4">
          <div className="space-y-1.5"><Label className="label-mono">Full name</Label>
            <Input required value={form.name} onChange={(e) => set("name", e.target.value)} data-testid="user-name-input" /></div>
          {!user && (
            <div className="space-y-1.5"><Label className="label-mono">Email</Label>
              <Input type="email" required value={form.email} onChange={(e) => set("email", e.target.value)} data-testid="user-email-input" /></div>
          )}
          <div className="space-y-1.5"><Label className="label-mono">{user ? "New password (optional)" : "Password"}</Label>
            <Input type="password" required={!user} minLength={8} value={form.password} onChange={(e) => set("password", e.target.value)} data-testid="user-password-input" /></div>
          <div className="space-y-1.5"><Label className="label-mono">Role</Label>
            <Select value={form.role} onValueChange={(v) => set("role", v)}>
              <SelectTrigger data-testid="user-role-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="employee" data-testid="user-role-option-employee">Employee (one depot)</SelectItem>
                <SelectItem value="admin" data-testid="user-role-option-admin">Admin / Owner (all depots)</SelectItem>
                <SelectItem value="truck_manager" data-testid="user-role-option-truck-manager">Truck Manager (trucks only)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {form.role === "employee" && <DepotField label="Assigned depot" value={form.depot_id} onChange={(v) => set("depot_id", v)} testid="user-depot-select" />}
          {user && (
            <div className="flex items-center justify-between rounded-md border border-slate-800 px-3 py-2">
              <Label className="label-mono">Account active</Label>
              <Switch checked={form.active} onCheckedChange={(v) => set("active", v)} data-testid="user-active-switch" />
            </div>
          )}
          <DialogFooter>
            <Button type="submit" disabled={busy} className="bg-amber-500 hover:bg-amber-400 text-slate-950 font-semibold" data-testid="user-save-button">
              {busy && <Loader2 className="h-4 w-4 animate-spin" />} Save
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
